import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import api, { errMsg } from "../lib/api";
import { fmtDate } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return fmtDate(iso); } catch { return iso; }
};

export default function Messages() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // {number, lead}
  const [thread, setThread] = useState(null); // {number, lead, messages}
  const [q, setQ] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [threadLoading, setThreadLoading] = useState(false);
  const requestVersion = useRef(0);
  const activeNumber = useRef(null);

  const loadThreads = useCallback(async () => {
    try { const { data } = await api.get("/messages/threads"); setThreads(data); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Opening Messages clears the unread badge for this user across the app.
  useEffect(() => {
    api.post("/messages/mark-read").catch(() => {});
    window.dispatchEvent(new Event("gsp:messages-read"));
  }, []);

  const openThread = useCallback(async (number) => {
    const version = ++requestVersion.current;
    activeNumber.current = number;
    setActive({ number });
    setThread(null); setThreadLoading(true); setText("");
    try { const { data } = await api.get(`/messages/thread/${encodeURIComponent(number)}`); if (version === requestVersion.current) setThread(data); }
    catch (e) { if (version === requestVersion.current) { setThread(null); setActive(null); activeNumber.current = null; toast.error(errMsg(e)); } }
    finally { if (version === requestVersion.current) setThreadLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      loadThreads();
      const number = activeNumber.current, version = requestVersion.current;
      if (!number) return;
      try {
        const { data } = await api.get(`/messages/thread/${encodeURIComponent(number)}`);
        if (version === requestVersion.current) setThread(data);
      } catch (e) {
        if (version === requestVersion.current && [403, 404].includes(e.response?.status)) {
          ++requestVersion.current; activeNumber.current = null;
          setThread(null); setActive(null); setText("");
        }
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [loadThreads]);

  const send = async () => {
    const to = (active?.number || "").trim();
    if (!to || !text.trim() || !thread || threadLoading) return;
    setSending(true);
    try {
      await api.post("/messages/send", { to_number: to, body: text.trim() });
      setText("");
      await openThread(to);
      loadThreads();
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  const startNew = async () => {
    const n = newNumber.trim();
    if (!n) return;
    setComposeOpen(false); setNewNumber("");
    await openThread(n);
  };

  const term = q.trim().toLowerCase();
  const filtered = threads.filter((t) => !term || [t.number, t.lead_name].filter(Boolean).join(" ").toLowerCase().includes(term));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Messages</h1>
          <p className="text-muted-foreground text-sm" data-testid="messages-privacy-scope">{isAdmin ? "All conversations, including unknown numbers" : "Conversations for leads assigned to you"}</p>
        </div>
        <button data-testid="new-message-btn" onClick={() => setComposeOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">
          <Icons.Plus size={18} /> New Message
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 lg:h-[calc(100vh-220px)] lg:min-h-[480px]">
        {/* thread list */}
        <div className="min-w-0 max-h-[52vh] lg:max-h-none bg-card border border-border rounded-md shadow-sm flex flex-col overflow-hidden" data-testid="thread-list">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Icons.Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="thread-search"
                placeholder="Search number or name…" className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
            {!loading && filtered.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No conversations yet.</div>}
            {filtered.map((t) => (
              <button key={t.key} onClick={() => openThread(t.number)} data-testid={`thread-${t.key}`}
                className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/40 transition-colors ${active?.number === t.number ? "bg-secondary/10" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold truncate">{t.lead_name || t.number}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0">{fmtWhen(t.last_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {t.lead_name && <span className="text-secondary">{t.number}</span>}
                  {!t.lead_name && <span data-testid={`unknown-thread-${t.key}`} className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">Unknown</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {t.last_direction === "outbound" ? "You: " : ""}{t.last_body}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* conversation */}
        <div className="min-w-0 min-h-[360px] lg:col-span-2 bg-card border border-border rounded-md shadow-sm flex flex-col overflow-hidden" data-testid="conversation">
          {!active && <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation or start a new message</div>}
          {active && (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="font-semibold">{thread?.lead?.name || active.number}</div>
                  <div className="text-xs text-muted-foreground">{active.number}</div>
                </div>
                {thread?.lead && <Link to={`/app/leads/${thread.lead.id}`} data-testid="open-lead"
                  className="text-xs text-secondary underline flex items-center gap-1"><Icons.ExternalLink size={13} /> Open lead</Link>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2.5" data-testid="messages-scroll">
                {threadLoading && <p data-testid="thread-loading" className="text-sm text-muted-foreground">Loading conversation…</p>}
                {(thread?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.direction === "outbound" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
                      <div data-testid={`message-body-${m.id}`} className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {m.direction === "outbound" ? (m.user_name || "You") : "Customer"} · {fmtWhen(m.created_at)}{m.status && m.status !== "sent" && m.status !== "received" ? ` · ${m.status}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                {thread && (thread.messages || []).length === 0 && <div className="text-sm text-muted-foreground text-center py-8">No messages yet — send the first one below.</div>}
              </div>
              <div className="p-3 border-t border-border flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  disabled={!thread || threadLoading} rows={1} placeholder="Type a message…" className="flex-1 min-w-0 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none max-h-32" />
                <button onClick={send} disabled={sending || !text.trim() || !thread || threadLoading} data-testid="send-message"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  <Icons.Send size={16} /> {sending ? "…" : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {composeOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setComposeOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-md w-full max-w-sm p-6 shadow-lg" data-testid="compose-modal">
            <h2 className="text-xl font-head font-extrabold mb-2">New Message</h2>
            <p className="text-sm text-muted-foreground mb-4">{isAdmin ? "Enter an Australian mobile number." : "Enter the mobile number of a lead assigned to you."}</p>
            <label className="label-caps">Mobile number</label>
            <input value={newNumber} onChange={(e) => setNewNumber(e.target.value)} data-testid="compose-number"
              onKeyDown={(e) => e.key === "Enter" && startNew()}
              placeholder="e.g. 0488 860 990" className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background text-sm" />
            <div className="flex gap-2 justify-end">
              <button data-testid="compose-cancel" onClick={() => setComposeOpen(false)} className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button onClick={startNew} disabled={!newNumber.trim()} data-testid="compose-start"
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-50">Start chat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
